import React, { forwardRef, useImperativeHandle, useRef, useEffect, useMemo } from 'react';
import { Canvas, useLoader } from '@react-three/fiber';
import { Physics, RigidBody } from '@react-three/rapier'; // Physics engine
import { OrbitControls } from '@react-three/drei';
import * as THREE from 'three';
import { Dice } from './Dice';

// --- 3D Graphics & Physics Concepts ---
//
// **Vector3:**
// A Vector3 is a fundamental concept in 3D graphics. It's an object with three values (x, y, z) that can represent:
// 1. A point in 3D space (e.g., the position of an object).
// 2. A direction in 3D space (e.g., the direction a camera is looking or the direction a face of a die is pointing).
// 3. A scale in 3D space (e.g., stretching an object).
//
// **Quaternion:**
// A Quaternion is a mathematical object used to represent rotation in 3D space. It has four values (x, y, z, w).
// While it's more complex than using simple angles (like Euler angles), it's preferred in 3D graphics because it avoids
// a problem called "gimbal lock," where you can lose a degree of rotational freedom. Quaternions also make for smooth interpolations between rotations.
//
// **Dot Product:**
// The dot product is a mathematical operation between two vectors. The result is a single number that tells you how much the two vectors are pointing
// in the same direction.
// - If the dot product is 1, the vectors are pointing in the exact same direction.
// - If it's 0, they are perpendicular.
// - If it's -1, they are pointing in opposite directions.
// We use this to find which face of the die is pointing "up" the most.

/**
 * A component representing the ground plane in the 3D scene.
 * It has a fixed rigid body so that other objects can collide with it.
 */
function Plane() {
  // Load the texture for the game board
  const texture = useLoader(THREE.TextureLoader, './assets/board.jpg');
  return (
    <RigidBody type="fixed" rotation={[-Math.PI / 2, 0, 0]}>
      <mesh receiveShadow>
        <planeGeometry args={[25,15]} />
        <meshStandardMaterial map={texture} />
      </mesh>
    </RigidBody>
  );
}

/**
 * A component for creating invisible walls to keep the dice within the scene.
 * @param {object} props - The component's props.
 * @param {number[]} props.position - The position of the wall.
 * @param {number[]} props.rotation - The rotation of the wall.
 * @param {number[]} props.args - The dimensions of the wall.
 */
function Wall({ position, rotation, args }) {
  return (
    <RigidBody type="fixed" position={position} rotation={rotation}>
      <mesh>
        <boxGeometry args={args} />
        <meshStandardMaterial transparent opacity={0} />
      </mesh>
    </RigidBody>
  );
}

// The order of faces for a Three.js box geometry is:
// 0: Right (+X), 1: Left (-X), 2: Top (+Y), 3: Bottom (-Y), 4: Front (+Z), 5: Back (-Z)
// This mapping is used to determine which symbol is facing up.
const getSymbolFromIndex = (index) => {
    const faceMap = ['spade', 'club', 'crown', 'flag', 'diamond', 'heart'];
    return faceMap[index];
};

/**
 * The main 3D scene for the dice game.
 * It sets up the camera, lighting, physics, and all the dice.
 * @param {object} props - The component's props.
 * @param {function} props.onRollComplete - A callback function to be called when the dice have settled.
 * @param {React.Ref} ref - A ref forwarded from the parent component.
 */
const DiceScene = forwardRef(({ onRollComplete }, ref) => {
    // Refs for each of the six dice
    const diceRefs = useRef([]);
    diceRefs.current = Array(6).fill().map((_, i) => diceRefs.current[i] || React.createRef());
    // Ref to track if the dice are currently being checked for being settled
    const isChecking = useRef(false);
  
    // Expose the rollDice method to the parent component
    useImperativeHandle(ref, () => ({
      rollDice: () => {
        isChecking.current = true;
        diceRefs.current.forEach((dieRef, index) => {
          if (dieRef.current) {
            // Pass the index for staggered positioning on reset
            dieRef.current.roll(index);
          }
        });
      },
    }));
  
    // Effect to check if the dice have settled after a roll
    useEffect(() => {
      const checkDiceSettled = () => {
        if (!isChecking.current) return;
  
        // Count how many dice are sleeping (i.e., have stopped moving)
        const settledCount = diceRefs.current.filter(ref => ref.current?.isSleeping).length;
        
        if (settledCount === 6) {
          isChecking.current = false;
          const finalResults = getDiceResults();
          onRollComplete(finalResults);
        }
      };
      
      const interval = setInterval(checkDiceSettled, 500);
      return () => clearInterval(interval);
    }, [onRollComplete]);
  
    /**
     * Determines the symbol facing up for each die.
     * @returns {string[]} An array of the symbols facing up on each die.
     */
    const getDiceResults = () => {
      return diceRefs.current.map((dieRef) => {
          const rotation = dieRef.current?.rotation;
          
          if (!rotation) {
              return null;
          }
  
          // The physics engine (Rapier) gives us the rotation as a Quaternion.
          // We convert it to a THREE.Quaternion to use it with THREE.js's vector math.
          const quaternion = new THREE.Quaternion(rotation.x, rotation.y, rotation.z, rotation.w);
  
          // This is a Vector3 representing the "up" direction in the world.
          const upVector = new THREE.Vector3(0, 1, 0);
          let maxDot = -Infinity;
          let topFaceIndex = -1;
  
          // These are the local direction vectors for each face of a non-rotated cube.
          // For example, the first vector (1, 0, 0) points along the positive X-axis, which is the "right" face.
          const faceVectors = [
              new THREE.Vector3(1, 0, 0),   // Right face
              new THREE.Vector3(-1, 0, 0),  // Left face
              new THREE.Vector3(0, 1, 0),   // Top face
              new THREE.Vector3(0, -1, 0),  // Bottom face
              new THREE.Vector3(0, 0, 1),   // Front face
              new THREE.Vector3(0, 0, -1),  // Back face
          ];
  
          // We loop through each face vector to see which one is pointing "up" the most after being rotated.
          for (let i = 0; i < faceVectors.length; i++) {
              // We take the local face vector and apply the die's actual rotation (the quaternion) to it.
              // This gives us a new vector that shows where that face is pointing in the 3D world.
              const worldVector = faceVectors[i].clone().applyQuaternion(quaternion);
              
              // We calculate the dot product between the rotated face vector and the world's "up" vector.
              // The closer this value is to 1, the more the face is pointing straight up.
              const dot = worldVector.dot(upVector);
  
              // We keep track of the face that has the highest dot product.
              if (dot > maxDot) {
                  maxDot = dot;
                  topFaceIndex = i;
              }
          }
          
          // The face with the highest dot product is the one facing up.
          return getSymbolFromIndex(topFaceIndex);
      });
    };
  
    // Memoize the dice components to prevent re-creation on re-renders
    const dice = useMemo(() => {
      return Array(6).fill().map((_, i) => {
        const dieRef = diceRefs.current[i] || React.createRef();
        diceRefs.current[i] = dieRef;
        return (
          <Dice
            key={i}
            ref={dieRef}
            position={[(Math.random() - 0.5) * 4, 2 + i * 2, (Math.random() - 0.5) * 4]}
          />
        );
      });
    }, []);
  
    return (
      <Canvas shadows camera={{ position: [0, 8, 8], fov: 50 }}>
        <ambientLight intensity={0.5} />
        <directionalLight 
          position={[10, 10, 5]} 
          intensity={1.5} 
          castShadow 
          shadow-mapSize-width={2048}
          shadow-mapSize-height={2048}
        />
        <OrbitControls />
        <Physics gravity={[0, -40, 0]}>
          <Plane />
          {/* Invisible walls to fully contain the dice within the play area */}
          {/* Ceiling wall - prevents dice from flying too high */}
          <Wall position={[0, 15, 0]} rotation={[-Math.PI / 2, 0, 0]} args={[30, 20, 0.1]} /> 
          {/* Back and front walls - aligned with the plane edges */}
          <Wall position={[0, 5, -7.5]} args={[25, 15, 0.1]} />
          <Wall position={[0, 5, 7.5]} args={[25, 15, 0.1]} />
          {/* Left and right walls - aligned with the plane edges */}
          <Wall position={[-12.5, 5, 0]} args={[0.1, 15, 15]} />
          <Wall position={[12.5, 5, 0]} args={[0.1, 15, 15]} />
          {/* Bottom safety wall - catches dice that clip through the ground at high velocity */}
          <Wall position={[0, -1, 0]} rotation={[-Math.PI / 2, 0, 0]} args={[30, 20, 0.1]} />

          {dice}
        </Physics>
      </Canvas>
    );
  });
  
  export default DiceScene;